import React, { useEffect, useRef, useState } from "react";
import { Button, Form, Modal } from "react-bootstrap";
import FileComponent from "./FileComponent";
import { IGroup, IGroupResponse } from "./interfaces";
import { _getBaseUrl, _post } from "./axios";
import { useToast } from "@/hooks/useToast";
import { getAccessToken } from "./localStorageServices";

function UploadModal({
  show,
  onHide,
  files,
  setFiles,
  getJobs,
}: {
  show: boolean;
  onHide: () => void;
  files: File | null;
  setFiles: React.Dispatch<React.SetStateAction<File | null>>;
  getJobs: () => void;
}) {
  const fileInputRef = useRef<HTMLInputElement | any>(); // Ref for the hidden file input element
  const [selectedValue, setSelectedValue] = useState(""); // State to manage selected dropdown value
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(0);
  const [models, setModels] = useState<IGroup[]>([]);

  const [isDragging, setIsDragging] = useState(false);

  const { toast } = useToast();

  const handleClick = () => {
    fileInputRef.current.click();
  };

  const filterFiles = () => {
    setFiles(null);
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFiles(file);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    setFiles(file);
  };

  const handleSelectChange = (e) => {
    setSelectedValue(e.target.value);
  };

  const handleUploadAll = async (e) => {
    e.preventDefault();

    if (!files || !selectedValue) {
      setUploading(false);
      return;
    }

    setUploading(true);
    setUploadProgress(0); // 👈 Set this to track progress in UI

    const formData = new FormData();
    formData.append("file", files);
    formData.append("model", selectedValue);

    const xhr = new XMLHttpRequest();
    const url = `${_getBaseUrl()}/categorizer/upload`;

    xhr.open("POST", url, true);
    xhr.setRequestHeader("Authorization", `Bearer ${getAccessToken()}`);

    // Track progress
    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable) {
        const percent = Math.round((event.loaded / event.total) * 100);
        setUploadProgress(percent); // 👈 Update progress bar
      }
    };

    xhr.onload = function () {
      setUploading(false);
      setUploadProgress(null);

      if (xhr.status === 200) {
        toast({
          type: "success",
          title: "CSV/Excel File Uploaded",
          message: "You have successfully uploaded a csv/ecxel file.",
        });
      } else {
        toast({
          type: "error",
          title: "CSV/Excel File Upload Failed",
          message: "Something went wrong while uploading csv/Excel file.",
        });
      }
      getJobs()
      setFiles(null);
      onHide();
    };

    xhr.onerror = function () {
      setUploading(false);
      setUploadProgress(null);
      toast({
        type: "error",
        title: "Upload Error",
        message: "There was a network or server error.",
      });
    };

    xhr.send(formData);
  };

  useEffect(() => {
    const getModels = async () => {
      try {
        const response = await _post<IGroupResponse>("/user_settings/groups", {
          school_id: "enflux",
          email: "placeholder.admin@enflux.com",
        });
        if (
          response.data?.status == 200 &&
          response?.data?.transaction == "Successful"
        ) {
          setModels(() => response.data.data.groups);
        }
      } catch (error: any) {
      }
    };

    getModels();
  }, []);

  return (
    <Modal
      show={show}
      onHide={onHide}
      aria-labelledby="contained-modal-title-vcenter"
      dialogClassName="modal-dialog"
      centered
    >
      <Modal.Header>
        <Modal.Title id="modal-title">Upload New CSV/Excel</Modal.Title>
      </Modal.Header>
      <Modal.Body className="body">
        <div
          className={`upload-area ${isDragging ? "dragover" : ""}`}
          onClick={handleClick}
          onDragOver={(e) => {
            e.preventDefault();
            setIsDragging(true);
          }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
        >
          <div className="upload-icon">
            <svg
              width="20"
              height="21"
              viewBox="0 0 20 21"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M5.80781 6.56719C5.7497 6.50915 5.7036 6.44022 5.67215 6.36434C5.6407 6.28847 5.62451 6.20714 5.62451 6.125C5.62451 6.04287 5.6407 5.96154 5.67215 5.88567C5.7036 5.80979 5.7497 5.74086 5.80781 5.68282L9.55781 1.93282C9.61586 1.87471 9.68479 1.82861 9.76066 1.79715C9.83654 1.7657 9.91787 1.74951 10 1.74951C10.0821 1.74951 10.1635 1.7657 10.2393 1.79715C10.3152 1.82861 10.3841 1.87471 10.4422 1.93282L14.1922 5.68282C14.3095 5.80009 14.3753 5.95915 14.3753 6.125C14.3753 6.29086 14.3095 6.44992 14.1922 6.56719C14.0749 6.68447 13.9159 6.75035 13.75 6.75035C13.5841 6.75035 13.4251 6.68447 13.3078 6.56719L10.625 3.8836V10.5C10.625 10.6658 10.5592 10.8247 10.4419 10.9419C10.3247 11.0592 10.1658 11.125 10 11.125C9.83424 11.125 9.67527 11.0592 9.55806 10.9419C9.44085 10.8247 9.375 10.6658 9.375 10.5V3.8836L6.69219 6.56719C6.63414 6.6253 6.56521 6.6714 6.48934 6.70285C6.41346 6.73431 6.33213 6.7505 6.25 6.7505C6.16787 6.7505 6.08654 6.73431 6.01066 6.70285C5.93479 6.6714 5.86586 6.6253 5.80781 6.56719ZM18.75 11.125V16.125C18.75 16.4565 18.6183 16.7745 18.3839 17.0089C18.1495 17.2433 17.8315 17.375 17.5 17.375H2.5C2.16848 17.375 1.85054 17.2433 1.61612 17.0089C1.3817 16.7745 1.25 16.4565 1.25 16.125V11.125C1.25 10.7935 1.3817 10.4755 1.61612 10.2411C1.85054 10.0067 2.16848 9.875 2.5 9.875H7.8125C7.89538 9.875 7.97487 9.90793 8.03347 9.96653C8.09208 10.0251 8.125 10.1046 8.125 10.1875V10.4578C8.125 11.5086 8.98438 12.3945 10.0359 12.375C10.527 12.3656 10.9947 12.1639 11.3386 11.8133C11.6825 11.4627 11.8751 10.9911 11.875 10.5V10.1875C11.875 10.1046 11.9079 10.0251 11.9665 9.96653C12.0251 9.90793 12.1046 9.875 12.1875 9.875H17.5C17.8315 9.875 18.1495 10.0067 18.3839 10.2411C18.6183 10.4755 18.75 10.7935 18.75 11.125ZM15.625 13.625C15.625 13.4396 15.57 13.2583 15.467 13.1042C15.364 12.95 15.2176 12.8298 15.0463 12.7589C14.875 12.6879 14.6865 12.6693 14.5046 12.7055C14.3227 12.7417 14.1557 12.831 14.0246 12.9621C13.8935 13.0932 13.8042 13.2603 13.768 13.4421C13.7318 13.624 13.7504 13.8125 13.8214 13.9838C13.8923 14.1551 14.0125 14.3015 14.1667 14.4045C14.3208 14.5075 14.5021 14.5625 14.6875 14.5625C14.9361 14.5625 15.1746 14.4637 15.3504 14.2879C15.5262 14.1121 15.625 13.8736 15.625 13.625Z"
                fill="#792C76"
              />
            </svg>
          </div>
          <span className="upload-instructions">
            <label htmlFor="file-upload" className="visually-hidden">
              Upload file
            </label>
            <input
              type="file"
              multiple // Allows selection of multiple files
              ref={fileInputRef} // Reference to access this input
              onChange={handleFileChange} // Handles file selection
              className="file-input"
              id="file-upload"
              accept=".csv, .xlsx, .xls" // Accepts CSV and Excel files
            />
            Click to upload <span>or drag and drop</span>
          </span>
          <span className="upload-file-type">CSV/Excel (max. 10MB)</span>
        </div>
        <div className="uploaded-container">
          {files && (
            <FileComponent
              key={files.name}
              size={`${(files.size / 1024).toFixed(2)}KB`}
              title={files.name}
              filterFiles={filterFiles}
              id={files.lastModified}
              uploading={uploading}
              uploadProgress={uploadProgress}
            />
          )}
        </div>
        <div className="model-selector">
          <Form>
            <Form.Group
              className="form-group-custom"
              controlId="exampleForm.SelectCustom"
            >
              
              <Form.Label className="form-label-custom">
                {" "}
                Categorization Model
              </Form.Label>
              <Form.Select
                value={selectedValue}
                onChange={handleSelectChange}
                aria-label="Default select example"
                className="form-select-custom"
              >
                <option>select categorization model</option>
                {models.map((group) =>
                  group.models.map((model) => (
                    <option key={model.model_id} value={model.model_key}>
                      {model.model_name}
                    </option>
                  ))
                )}
              </Form.Select>
            </Form.Group>
          </Form>
        </div>
      </Modal.Body>
      <Modal.Footer id="modal-footer">
        <Button className="cancel-btn" onClick={onHide}>
          Close
        </Button>
        <Button
          className="submit-btn"
          type="submit"
          disabled={!selectedValue || !files || uploading}
          onClick={handleUploadAll}
        >
         {uploading ? "Uploading..." : "Upload"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}

export default UploadModal;
